import Table from "@/ui/table-div.tsx";
import {
  useGetInfiniteWalletScreener,
  WALLET_SCREENER_LIMIT,
  WalletSortColumn,
} from "@api/wallet-screener/wallet-screener.ts";
import { Wallet } from "@api/wallet-screener/types.ts";
import { Index, Pnl, PnlInPer, Value, WinRate } from "@/ui/formats.tsx";
import { IconComponent, IconsTypes } from "@/ui/icon";
import {
  displayWalletAddress,
  getFavoriteWallets,
  toggleFavoriteWallet,
} from "@utils/wallet.ts";
import {
  useWalletDiscoveryFilter,
  WalletDiscoveryFiltersParams,
} from "@providers/wallet-discovery-filter/wallet-discovery-filter-provider.tsx";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, UnstyledButton } from "@mantine/core";
import clsx from "clsx";
import GradientBorderRadius from "@ui/gradient-border-radius.tsx";
import RawCopyTradeModal from "@features/copy-trade-modal/raw-copy-trade-modal.tsx";
import InfiniteScroll from "react-infinite-scroll-component";
import { useInViewPort } from "@/hooks";
import { useLocation } from "react-router";
import { WalletType } from "@providers/wallet";

const WalletScreenerTable = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.includes("/admin");

  const {
    filters,
    timeFrame,
    searchWallet,
    chains,
    showStarred,
    onFiltersUpdate,
    setFetchedWallets,
    selectedWallets,
    setSelectedWallets,
  } = useWalletDiscoveryFilter();

  const {
    data: wallets,
    fetchNextPage,
    hasNextPage,
    isPlaceholderData,
    isFetching: isFetchingInfinite,
  } = useGetInfiniteWalletScreener({
    limit: WALLET_SCREENER_LIMIT,
    timeFrame: timeFrame,
    searchWallet: searchWallet,
    filters: filters,
  });

  const isFetching = isFetchingInfinite || isPlaceholderData;

  const [favorites, setFavorites] = useState<string[]>(getFavoriteWallets());

  const filteredWallets = useMemo(() => {
    const base = (wallets ?? []).filter(
      (wallet) => wallet.chain && chains.includes(wallet.chain),
    );

    return showStarred
      ? base.filter((w) => getFavoriteWallets().includes(w.address))
      : base;
  }, [wallets, chains, showStarred]);

  const toggleFavorite = (address: string) => {
    const updated = toggleFavoriteWallet(address);
    setFavorites(updated);
  };

  const lastRowRef = useRef<HTMLDivElement | null>(null);

  const isInViewPort = useInViewPort(lastRowRef);

  useEffect(() => {
    if (isInViewPort) {
      fetchNextPage();
    }
  }, [isInViewPort, fetchNextPage]);

  useEffect(() => {
    const mapped = new Map<string, number>(
      wallets?.map((w) => [w.address, w.chain === WalletType.ETH ? 1 : 2]) ??
        [],
    );

    setFetchedWallets(mapped);
  }, [wallets]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedWalletAddress, setSelectedWalletAddress] =
    useState<string>("");

  const handleSortToggle = (column: WalletSortColumn) => {
    return () => {
      onFiltersUpdate((prev: WalletDiscoveryFiltersParams) => {
        const currentSort: WalletDiscoveryFiltersParams["sort"] =
          prev?.sort ?? { column: "netPnlPercent", direction: "desc" };

        const newDirection =
          currentSort?.column === column
            ? currentSort?.direction === "asc"
              ? "desc"
              : "asc"
            : "desc";

        const newSort = {
          column: column,
          direction: newDirection,
        };

        return { ...prev, sort: newSort };
      });
    };
  };

  const handleSelectWallet = (address: string) => {
    setSelectedWallets((prev: Map<string, boolean>) => {
      const updated = new Map(prev);
      const current = updated.get(address) ?? false;

      if (current) {
        updated.delete(address);
      } else {
        updated.set(address, true);
      }

      return updated;
    });
  };

  return (
    <>
      <RawCopyTradeModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        walletAddress={selectedWalletAddress}
      />
      <Table className="text-left">
        <Table.Header className="h-[1.5625rem] bg-black items-center">
          <Table.HeaderColumn className="w-[2.4%] flex justify-center items-center">
            #
          </Table.HeaderColumn>
          <Table.HeaderColumn className="w-[15.205%]">
            Wallet
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("invested")();
            }}
          >
            Invested
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "invested" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("trades")();
            }}
          >
            Trades
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "trades" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("winRate")();
            }}
          >
            Win Rate
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "winRate" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("realizedPnl")();
            }}
          >
            Realized PNL
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "realizedPnl" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("realizedPnlPercent")();
            }}
          >
            Realized ROI
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "realizedPnlPercent" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("unrealizedPnl")();
            }}
          >
            Unrealized PNL
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "unrealizedPnl" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("unrealizedPnlPercent")();
            }}
          >
            Unrealized ROI
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "unrealizedPnlPercent" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("netPnl")();
            }}
          >
            Net PNL
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "netPnl" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          <Table.HeaderColumn
            className={clsx(
              "cursor-pointer flex items-center gap-1",
              isAdmin ? "w-[8.5447%]" : "w-[9.155%]",
            )}
            onClick={() => {
              handleSortToggle("netPnlPercent")();
            }}
          >
            Net ROI
            <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
              {filters?.sort?.column === "netPnlPercent" &&
                (filters?.sort?.direction === "asc" ? (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowUp"
                    color="white"
                  />
                ) : (
                  <IconComponent
                    className="w-[0.375rem] h-[0.375rem]"
                    name="arrowDown"
                    color="white"
                  />
                ))}
            </div>
          </Table.HeaderColumn>
          {isAdmin && (
            <Table.HeaderColumn className="w-[5.493%] cursor-pointer flex items-center justify-end gap-1 pr-2">
              Select
            </Table.HeaderColumn>
          )}
        </Table.Header>
        {isFetching && (
          <div className="w-full h-[0.0625rem] bg-black overflow-hidden">
            <div className="w-full h-full bg-[#5AC4BE] animate-[loading_1s_ease-in-out_infinite]" />
          </div>
        )}
        <Table.Body id="wallet-discovery-table">
          <InfiniteScroll
            dataLength={wallets?.length ?? 0}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={
              <div
                key={`loading-element`}
                className="text-sm h-[2rem] flex justify-center items-center"
              >
                Loading Wallets...
              </div>
            }
            scrollableTarget="wallet-discovery-table"
          >
            {filteredWallets?.map((item: Wallet, index: number) => {
              const isFavourite = favorites.includes(item.address);

              return (
                <Fragment key={`row-${item.address}-fragment`}>
                  <Table.Row
                    key={`row-${item.address}`}
                    onClick={() => {
                      setSelectedWalletAddress(item.address);
                      setIsModalOpen(true);
                    }}
                    className={clsx(
                      "text-xs z-0 flex items-center py-[0.30rem]",
                    )}
                  >
                    <Table.Column className="w-[2.4%] flex justify-center items-center font-semibold">
                      <Index value={index + 1} />
                    </Table.Column>
                    <Table.Column className="w-[15.205%] flex items-center font-semibold">
                      <div className="flex gap-[0.3125rem] justify-start items-center">
                        <div className="w-[1.1875rem] h-[1.1875rem] rounded-full  justify-center items-center bg-black hidden">
                          <IconComponent
                            name={item.chain as IconsTypes}
                            className={
                              item.chain === "solana"
                                ? "w-[0.6875rem] h-[0.6875rem]"
                                : "w-[1.1875rem] h-[1.1875rem]"
                            }
                          />
                        </div>
                        <div className="w-[1.1875rem] h-[1.1875rem] rounded-full flex justify-center items-center bg-black">
                          <IconComponent
                            name="solanaLogo"
                            className="w-[1.1875rem] h-[1.1875rem]"
                          />
                        </div>
                        {displayWalletAddress(item.address || "")}
                        <GradientBorderRadius
                          hover="r-100"
                          radius="circle"
                          gradient="none"
                          background="black"
                        >
                          <UnstyledButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(item.address ?? "");
                            }}
                            className={clsx(
                              "w-[1.125rem] h-[1.125rem] flex justify-center items-center hover:opacity-100",
                              "opacity-100",
                            )}
                          >
                            <IconComponent
                              name="starEmpty"
                              className="w-[0.5625rem] h-[0.5625rem]"
                              fill={isFavourite ? "white" : "none"}
                            />
                          </UnstyledButton>
                        </GradientBorderRadius>
                      </div>
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <Value value={item.invested} withSign={false} />
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      {item.trades}
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <WinRate value={item.winRate} />
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <Pnl value={item.realizedPNL.amount} />
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <PnlInPer value={item.realizedPNL.percentage} />
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <Pnl value={item.unrealizedPNL.amount} />
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <PnlInPer value={item.unrealizedPNL.percentage} />
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <Pnl value={item.netPNL.amount} />
                    </Table.Column>
                    <Table.Column
                      className={clsx(isAdmin ? "w-[8.5447%]" : "w-[9.155%]")}
                    >
                      <PnlInPer value={item.netPNL.percentage} />
                    </Table.Column>
                    {isAdmin && (
                      <Table.Column className="flex w-[5.493%] items-center justify-end pr-4">
                        <GradientBorderRadius radius="normal" gradient="50">
                          <Checkbox
                            size="xs"
                            variant="filled"
                            classNames={{
                              input:
                                "bg-transparent border-none ring-0 focus:ring-0 focus:outline-none",
                            }}
                            styles={{
                              input: {
                                backgroundColor: "transparent",
                                border: "none",
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={() => {
                              handleSelectWallet(item.address);
                            }}
                            checked={selectedWallets.get(item.address) === true}
                          />
                        </GradientBorderRadius>
                      </Table.Column>
                    )}
                  </Table.Row>
                </Fragment>
              );
            })}
          </InfiniteScroll>
        </Table.Body>
      </Table>
    </>
  );
};

export default WalletScreenerTable;
